import { DottedBox, Button, FormBox, FoodsIcon } from "@foodpilot/foods";
import { Box, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ConsumptionModal } from "./ConsumptionModal";
import { ProductFormType } from "../../ProductForm.types";
import { ConsumptionReference, useConsumptionReference } from "src/api/sure-api/useConsumptionReference";
import { Loading } from "src/components/utils/Loading";
import DoubleCheck from "src/assets/icons/double-check.svg?react";
import { formatFieldLabel } from "src/components/EntityForm/FoodsFieldPrimitives";

type Page6Props = {
    product: ProductFormType;
    setProduct: (newProduct: ProductFormType) => void;
};
export const Page6 = (props: Page6Props) => {
    const { t } = useTranslation();

    const { product, setProduct } = props;
    const { productConsumptions, title } = product;

    const [isOpen, setOpen] = useState(false);
    const { consumptionReference, isLoading } = useConsumptionReference();

    if (isLoading === true) {
        return <Loading />;
    }

    if (consumptionReference === undefined) {
        // Error state
        return <Typography variant="body-medium">{t("common.unexpected_error", "Unexpected error")}</Typography>;
    }

    const selectedReferenceIds = productConsumptions.map((pc) => pc.consumptionReferenceId);
    const consumptionsToDisplay = consumptionReference.filter((cons) => selectedReferenceIds.includes(cons.id));
    return (
        <Box>
            <ConsumptionModal
                isOpen={isOpen}
                setOpen={setOpen}
                currentConsumptions={selectedReferenceIds}
                allConsumptions={consumptionReference}
                infoText={title}
                onValidate={(newSelectedConsumptionIds) => {
                    const existingByRef = new Map(productConsumptions.map((pc) => [pc.consumptionReferenceId, pc]));
                    const newProductConsumptions = newSelectedConsumptionIds.map((refId) => {
                        const existing = existingByRef.get(refId);
                        if (existing !== undefined) {
                            return existing;
                        }
                        return { consumptionReferenceId: refId };
                    });
                    setProduct({
                        ...product,
                        productConsumptions: newProductConsumptions,
                    });
                }}
            />
            {consumptionsToDisplay.length === 0 ?
                <NoConsumptions onOpen={() => setOpen(true)} />
            :   <DisplayConsumptions consumptions={consumptionsToDisplay} onOpen={() => setOpen(true)} />}
        </Box>
    );
};

type DisplayConsumptionsProps = {
    consumptions: ConsumptionReference[];
    onOpen: () => void;
};
const DisplayConsumptions = (props: DisplayConsumptionsProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { consumptions, onOpen } = props;

    return (
        <FormBox>
            <Typography variant="h3" sx={{ marginBottom: "25px" }}>
                {formatFieldLabel(t("product.consumption.modes_label", "Product use methods:"))}
            </Typography>
            <Box sx={{ borderTop: `1px solid ${theme.custom.grey[500]}` }}>
                <Box
                    sx={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "end",
                        marginBlock: "18px",
                    }}
                >
                    <Button variant="ternary" startIcon={<DoubleCheck />} onClick={onOpen}>
                        <Typography variant="body-bold">
                            {t("product.consumption.add_remove_modes", "Add or remove consumption modes")}
                        </Typography>
                    </Button>
                </Box>
                <Box sx={{ width: "100%", display: "flex", gap: "10px", flexDirection: "column" }}>
                    {consumptions.map((value) => {
                        return (
                            <Box
                                key={value.id}
                                sx={{
                                    display: "flex",
                                    justifyContent: "left",

                                    paddingLeft: "28px",
                                    paddingBlock: "15px",
                                    border: `1px solid ${theme.custom.grey[500]}`,
                                    borderRadius: "8px",
                                }}
                            >
                                <Typography key={`consumption-reference-${value.id}`} variant="h4">
                                    {value.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </FormBox>
    );
};

type NoConsumptionsProps = {
    onOpen: () => void;
};
const NoConsumptions = (props: NoConsumptionsProps) => {
    const { t } = useTranslation();
    const { onOpen } = props;
    return (
        <DottedBox>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "2rem",
                }}
            >
                <Typography variant="big-medium">
                    {t("product.consumption.no_mode", "No mode of consumption")}
                </Typography>
                <Button variant="text" startIcon={<FoodsIcon icon="plus" size={2} />} onClick={onOpen}>
                    {t("product.consumption.add_mode", "Add a consumption mode")}
                </Button>
            </div>
        </DottedBox>
    );
};
